import { vec2, vec3 } from 'gl-matrix';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ToolManager } from '~/engine/tool/ToolManager';
import { AdaptiveBrushTool } from '~/engine/tool/tools/contouring-tools/AdaptiveBrushTool';
import { StaticBrushTool } from '~/engine/tool/tools/contouring-tools/StaticBrushTool';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import useEventListener from '~/hooks/useEventListener';
import useViewManagerEvents from '~/hooks/useViewManagerEvents';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import useWindowLevel from '~/hooks/useWindowLevel';
import {
    lowerSideViewState,
    mainViewState,
    upperSideViewState,
    viewManagerState,
} from '~/state/atoms/engineAtoms';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import {
    brushDiameterLabelVisibleState,
    brushOptionsState,
} from '~/state/atoms/toolsAtoms';
import {
    currentImageSliceOrientationPositionsState,
    imageSlicesCountState,
} from '~/state/atoms/viewerAtoms';
import { calculateNormalizedOrientationValue } from '~/utils/units';
import ViewportOverlay from './ViewportOverlay';

export type ImageSliceIdxProps = {
    [key: string]: number;
};

export type ViewIndicesProps = {
    [key: string]: vec2 | null;
};

export type PatientSpaceCoordsProps = {
    coords: vec3 | null;
    canvasCoords?: vec2 | null;
    orthoCoords?: vec3 | null;
};

export type ImageSlicePositions = {
    [key in Orientations]: string;
};

interface ViewportProps {
    view: PlaneView | null;
    viewportOrientation: Orientations;
    canvas: React.RefObject<HTMLCanvasElement | null>;
    canvas2D: React.RefObject<HTMLCanvasElement | null>;
}

const Viewport = ({
    view,
    viewportOrientation,
    canvas,
    canvas2D,
}: ViewportProps) => {
    useViewManagerEvents();

    const [isActive, setIsActive] = useState(false);

    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const patientRepository = useRecoilValue(patientRepositoryState);

    const { environment } = useViewportEnvironment();

    const viewManager = useRecoilValue(viewManagerState);
    const mainView = useRecoilValue(mainViewState);
    const upperSideView = useRecoilValue(upperSideViewState);
    const lowerSideView = useRecoilValue(lowerSideViewState);
    const { windowLevel } = useWindowLevel();

    const [selectedROI, setSelectedRoi] = useRecoilState(selectedROIState);
    const [brushOptions] = useRecoilState(brushOptionsState);
    const [brushDiameterLabelVisible, setBrushDiameterLabelVisible] =
        useRecoilState(brushDiameterLabelVisibleState);

    const setImageSlicesCount = useSetRecoilState(imageSlicesCountState);

    const [
        currentImageSliceOrientationPositions,
        setCurrentImageSliceOrientationPositions,
    ] = useRecoilState(currentImageSliceOrientationPositionsState);

    useEffect(() => {
        if (environment) {
            environment.setSelectedRoiByNumber(selectedROI);
        }
    }, [environment, selectedROI]);

    const handleStructureSetPropertyChanged = () => {
        if (
            environment?.structureSet?.selectedStructure?.roiNumber ===
            selectedROI
        ) {
            return;
        }
        setSelectedRoi(
            environment?.structureSet?.selectedStructure?.roiNumber ?? -1
        );
    };

    useEventListener(
        'structure_set_property_changed',
        handleStructureSetPropertyChanged,
        environment?.eventTarget
    );

    useEffect(() => {
        setSelectedPatient();
    }, [selectedPatientId]);

    useEffect(() => {
        if (environment && windowLevel) {
            environment.updateImageObjectsWindowingLevel(windowLevel);
        }
    }, [windowLevel]);

    /*
     * This useEffect is used to add event listeners to the viewManager.
     * The event listeners are used to update the crosshairs when the plane intersection point changes.
     * The event listeners are also used to update the crosshairs when the view is panned or zoomed.
     */
    useEffect(() => {
        if (viewManager && mainView && upperSideView && lowerSideView) {
            if (viewManager && mainView && upperSideView && lowerSideView) {
                viewManager.addEventListener(
                    'planeIntersectionPointChanged',
                    handlePlaneIntersectionPointChanged
                );
            }
            return () => {
                if (viewManager) {
                    viewManager.removeEventListener(
                        'planeIntersectionPointChanged',
                        handlePlaneIntersectionPointChanged
                    );
                }
            };
        }
    }, [viewManager, mainView, upperSideView, lowerSideView]);

    const handlePlaneIntersectionPointChanged = () => {
        setCurrentImageSliceOrientationPositions(() =>
            getNewCurrentImageSlice()
        );
    };

    useEffect(() => {
        updateBrushDiameter();
    }, [brushOptions.diameter]);

    useEffect(() => {
        const brushDiameterLabelVisibleTimeout = setTimeout(() => {
            setBrushDiameterLabelVisible(false);
        }, 2000);
        return () => clearTimeout(brushDiameterLabelVisibleTimeout);
    }, [brushOptions.diameter, brushDiameterLabelVisible]);

    const updateBrushDiameter = () => {
        if (!viewManager || !environment || !environment.image) return;
        if (ToolManager.getActiveTool() instanceof StaticBrushTool) {
            (ToolManager.getActiveTool() as StaticBrushTool).diameter =
                brushOptions.diameter;
        } else if (ToolManager.getActiveTool() instanceof AdaptiveBrushTool) {
            (ToolManager.getActiveTool() as AdaptiveBrushTool).diameter =
                brushOptions.diameter;
        }
        setBrushDiameterLabelVisible(true);
    };

    const setSelectedPatient = () => {
        if (patientRepository) {
            const patients = patientRepository.getAll();
            const patient = patients.find(
                (p) => p.dataSet.PatientID === selectedPatientId
            );
            const patientCTSeries = patient?.dataSet?.series.find(
                (series) => series.seriesModule.modality === 'CT'
            );
            if (patientCTSeries) {
                const numberOfImgSlices = patientCTSeries.numberOfObjects;
                setImageSlicesCount(numberOfImgSlices - 1);
            }
        }
    };

    const getNewCurrentImageSlice = (): ImageSlicePositions => {
        if (!environment || !mainView || !upperSideView || !lowerSideView) {
            return currentImageSliceOrientationPositions;
        }
        const imgObject = environment.image;
        if (!imgObject) {
            return currentImageSliceOrientationPositions;
        }
        const views = [mainView, upperSideView, lowerSideView];
        const newImageSliceOrientationPositions: ImageSlicePositions = {
            [Orientations.TRANSVERSAL_ORIENTATION]:
                currentImageSliceOrientationPositions.TRANSVERSAL_ORIENTATION,
            [Orientations.SAGITTAL_ORIENTATION]:
                currentImageSliceOrientationPositions.SAGITTAL_ORIENTATION,
            [Orientations.CORONAL_ORIENTATION]:
                currentImageSliceOrientationPositions.CORONAL_ORIENTATION,
        };
        for (const orientation of Object.values(Orientations)) {
            const planeCoordinates = views.find(
                (view) => view.label === orientation
            )?.planeCoordinates;
            if (planeCoordinates === undefined) {
                return currentImageSliceOrientationPositions;
            }
            newImageSliceOrientationPositions[orientation] =
                calculateNormalizedOrientationValue(planeCoordinates);
        }
        return newImageSliceOrientationPositions;
    };

    return (
        <div
            className={`viewport-container ${
                isActive ? 'active-viewport' : ''
            }`}
            onMouseEnter={() => setIsActive(true)}
            onMouseLeave={() => setIsActive(false)}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
            }}
        >
            <ViewportOverlay
                view={view}
                viewportOrientation={viewportOrientation}
                canvas={canvas}
                canvas2D={canvas2D}
                isActive={isActive}
                getNewCurrentImageSlice={getNewCurrentImageSlice}
            />
        </div>
    );
};

export default Viewport;
